/**
 * Web Vitals reporting to Google Analytics 4
 * 
 * This module captures Core Web Vitals (LCP, FID, CLS, FCP, TTFB, INP) 
 * and sends them to GA4 for performance monitoring.
 * 
 * Metrics:
 * - LCP (Largest Contentful Paint): Loading performance
 * - FID (First Input Delay): Interactivity (legacy, replaced by INP)
 * - CLS (Cumulative Layout Shift): Visual stability
 * - FCP (First Contentful Paint): Initial render
 * - TTFB (Time to First Byte): Server response time
 * - INP (Interaction to Next Paint): Responsiveness
 */

import { onCLS, onFCP, onLCP, onTTFB, onINP, type Metric } from 'web-vitals';
import { shouldEnableTracking } from '@/lib/runtimeEnvironment';

// Check if gtag is available AND tracking is allowed
const isGtagAvailable = (): boolean => {
  return typeof window !== 'undefined' && typeof window.gtag === 'function' && shouldEnableTracking();
};

/**
 * Send Web Vital metric to Google Analytics 4
 */
const sendToGA4 = (metric: Metric): void => {
  if (!isGtagAvailable()) {
    // Log to console in development for debugging
    if (import.meta.env.DEV) {
      console.log('[Web Vitals]', metric.name, Math.round(metric.value), metric.rating);
    }
    return;
  }

  // Send as GA4 event with standardized naming
  window.gtag('event', metric.name, {
    // Use `event_category` for grouping in GA4 reports
    event_category: 'Web Vitals',
    // The `value` field expects an integer in GA4
    value: Math.round(metric.name === 'CLS' ? metric.value * 1000 : metric.value),
    // Non-interaction event to not affect bounce rate
    non_interaction: true,
    // Custom dimensions for detailed analysis
    metric_id: metric.id,
    metric_value: metric.value,
    metric_rating: metric.rating, // 'good', 'needs-improvement', or 'poor'
    metric_delta: metric.delta,
    // Navigation type for context
    navigation_type: metric.navigationType,
    // Page path for per-page analysis
    page_path: window.location.pathname,
  });

  // Also send as a more detailed custom event for advanced analysis
  window.gtag('event', 'web_vitals_detailed', {
    metric_name: metric.name,
    metric_value: metric.value,
    metric_rating: metric.rating,
    metric_delta: metric.delta,
    metric_id: metric.id,
    page_path: window.location.pathname,
    page_url: window.location.href,
  });
};

/**
 * Initialize Web Vitals reporting
 * Call this once at app startup to begin collecting metrics
 */
export const initWebVitals = (): void => {
  // Only run in browser environment
  if (typeof window === 'undefined') return;

  // Core Web Vitals (the 3 main metrics Google uses for ranking)
  onLCP(sendToGA4);  // Largest Contentful Paint
  onINP(sendToGA4);  // Interaction to Next Paint (replaced FID)
  onCLS(sendToGA4);  // Cumulative Layout Shift

  // Additional performance metrics
  onFCP(sendToGA4);  // First Contentful Paint
  onTTFB(sendToGA4); // Time to First Byte
};

/**
 * Get Web Vitals thresholds for reference
 * Based on Google's Core Web Vitals thresholds
 */
export const WEB_VITALS_THRESHOLDS = {
  LCP: { good: 2500, poor: 4000 },      // milliseconds
  INP: { good: 200, poor: 500 },        // milliseconds
  CLS: { good: 0.1, poor: 0.25 },       // score (unitless)
  FCP: { good: 1800, poor: 3000 },      // milliseconds
  TTFB: { good: 800, poor: 1800 },      // milliseconds
} as const;
